import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, ExternalLink, Camera, Video } from "lucide-react";

/**
 * Full-screen photo viewer with prev/next navigation.
 *
 * Props:
 *   items: Array<{ id, name, photo_url, photo_card_url?, uploaded_at? }>
 *          — all horses with photos, in display order
 *   index: number  — index of the currently-shown item (0-based)
 *   onClose: () => void
 *   onIndex: (newIndex: number) => void  — called when arrows move
 *
 * Keyboard:
 *   ←  /  →   prev / next
 *   Esc       close
 *
 * Click outside the image (on the backdrop) closes.
 */
export function PhotoLightbox({ items, index, onClose, onIndex }) {
  const hasItems = Array.isArray(items) && items.length > 0;
  const safeIdx = hasItems ? Math.min(Math.max(0, index | 0), items.length - 1) : 0;
  const current = hasItems ? items[safeIdx] : null;
  const total = hasItems ? items.length : 0;
  const hasMultiple = total > 1;

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    onIndex((safeIdx - 1 + total) % total);
  }, [safeIdx, total, hasMultiple, onIndex]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    onIndex((safeIdx + 1) % total);
  }, [safeIdx, total, hasMultiple, onIndex]);

  // Keyboard nav. We attach to window so the lightbox owns the keys
  // while it's open. body-scroll-lock is applied for UX polish.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Flag the body so other window-level arrow-key handlers (e.g. the
    // horse-to-horse nav strip) can yield to the lightbox while it's open.
    document.body.setAttribute("data-lightbox-open", "1");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      document.body.removeAttribute("data-lightbox-open");
    };
  }, [goPrev, goNext, onClose]);

  if (!current) return null;

  const isVideo = current.kind === "video" || Boolean(current.video_url);
  const fullUrl = current.photo_url || `/api/horses/${current.id}/photo/full`;
  const uploadedAt = current.photo?.uploaded_at || current.photo_uploaded_at || current.uploaded_at;
  const dateStr = uploadedAt
    ? new Date(uploadedAt).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo of ${current.name}`}
    >
      {/* Close (top right) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/90 ring-1 ring-white/20 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label="Close lightbox"
        data-testid="photo-lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Prev arrow */}
      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/90 ring-1 ring-white/20 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label="Previous photo"
          data-testid="photo-lightbox-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image + caption */}
      <div
        className="relative max-h-[88vh] max-w-[92vw] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            key={current.id}
            src={current.video_url}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] max-w-[92vw] rounded-lg bg-black object-contain shadow-2xl"
            data-testid="photo-lightbox-video"
          />
        ) : (
          <img
            key={current.id}
            src={fullUrl}
            alt={current.name}
            className="max-h-[80vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
            data-testid="photo-lightbox-img"
          />
        )}
        <div className="mt-4 flex flex-col items-center gap-1 text-center text-white/90">
          <Link
            to={`/horses/${current.horse_id || current.id}`}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-base font-medium hover:underline"
            data-testid="photo-lightbox-name-link"
          >
            {current.title || current.name}
            <ExternalLink className="h-3.5 w-3.5 opacity-70" />
          </Link>
          {dateStr && (
            <div className="text-xs text-white/70 inline-flex items-center gap-1" data-testid="photo-lightbox-date">
              {isVideo
                ? <Video className="h-3 w-3" strokeWidth={1.5} />
                : <Camera className="h-3 w-3" strokeWidth={1.5} />} {dateStr}
            </div>
          )}
          {hasMultiple && (
            <div className="text-[11px] text-white/50">
              {safeIdx + 1} of {total} · use ← → to navigate
            </div>
          )}
        </div>
      </div>

      {/* Next arrow */}
      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white/90 ring-1 ring-white/20 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label="Next photo"
          data-testid="photo-lightbox-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}

/**
 * Convenience hook: given an array `horses` and the currently-focused
 * horse id, return the items array (horses with photos) and the open/close
 * state machine for the lightbox.
 *
 *    const lightbox = useHorsePhotoLightbox(horses, currentHorseId);
 *    <img onClick={lightbox.open} ... />
 *    {lightbox.element}
 */
export function useHorsePhotoLightbox(horses, currentHorseId) {
  const [openIdx, setOpenIdx] = useState(null);
  const items = (horses || []).filter((h) => h && (h.photo || h.photo_url || h.photo_card_url));

  const open = useCallback(() => {
    const i = items.findIndex((h) => h.id === currentHorseId);
    setOpenIdx(i >= 0 ? i : 0);
  }, [items, currentHorseId]);

  const close = useCallback(() => setOpenIdx(null), []);
  const onIndex = useCallback((i) => setOpenIdx(i), []);

  const element = openIdx !== null
    ? <PhotoLightbox items={items} index={openIdx} onClose={close} onIndex={onIndex} />
    : null;

  return { open, close, element, items, isOpen: openIdx !== null };
}
